import * as menuBoard from "../lib/api/menuBoard";
import AlertModal from "./AlertModal";
import ImageSetUploader from "./ImageSetUploader";
import PropTypes from "prop-types";
import StyledDropdown from "./StyledDropdown";
import VideoUploader from "./VideoUploader";
import React, { PureComponent } from "react";
import { Button, Col, ControlLabel, FormControl, FormGroup, Modal, Row } from "react-bootstrap";
import { StyleSheet, css } from "aphrodite";

const strings = {
    mediaType: "Media Type",
    selectMediaType: "Select Media Type",
    name: "Name",
    duration: "Duration (ms)",
    description: "Description",
};

const styles = StyleSheet.create({
    buttonRow: {
        display: "flex",
        flexDirection: "row",
        gap: "16px",
        justifyContent: "center",
    },
    topRow: {
        marginBottom: "16px",
    },
});

const mediaTypeOptions = [
    {
        label: "Image",
        value: "image",
    },
    {
        label: "Video",
        value: "video",
    },
];

export default class AddFeatureAssetModal extends PureComponent {
    static propTypes = {
        show: PropTypes.bool.isRequired,
        onSaveCallback: PropTypes.func.isRequired,
        handleClose: PropTypes.func.isRequired,
        sectionId: PropTypes.string.isRequired,
        menuName: PropTypes.string.isRequired,
        width: PropTypes.number.isRequired,
        height: PropTypes.number.isRequired,
    };

    state = {
        mediaType: "",
        menuName: this.props.menuName,
        nameInput: "",
        durationInput: 0,
        descriptionInput: "",
        showAlertModal: false,
        errorMessage: "",
        uploadedImage: undefined,
        uploadedVideo: undefined,
        uploadedVideoDurationMs: undefined,
        loading: false,
    };

    _handleChangeMediaTypeDropdown = value => {
        this.setState({ mediaType: value });
    };

    _handleNameChange = e => {
        this.setState({ nameInput: e.target.value });
    };

    _handleDurationChange = e => {
        this.setState({ durationInput: e.target.value });
    };

    _handleDescriptionChange = e => {
        this.setState({ descriptionInput: e.target.value });
    };

    _handleImageUpdate = (image, crop, imageData) => {
        this.setState({
            uploadedImage: {
                image,
                centerX: crop.x,
                centerY: crop.y,
                width: crop.width,
                height: crop.height,
                imageData: imageData,
            },
        });
    };

    _handleVideoUpdate = (video, durationMs) => {
        this.setState({
            uploadedVideo: video,
            uploadedVideoDurationMs: durationMs,
        });
    };

    _addFeatureAsset = () => {
        this.setState({
            loading: true,
        });
        menuBoard
            .addFeatureAsset(this.props.sectionId, {
                name: this.state.nameInput,
                description: this.state.descriptionInput,
                image: this.state.uploadedImage,
                video: this.state.uploadedVideo,
                durationMs:
                    this.state.mediaType === "image"
                        ? this.state.durationInput
                        : this.state.uploadedVideoDurationMs,
            })
            .then(addedAsset => {
                this.props.onSaveCallback(addedAsset);
            })
            .catch(error => {
                this.setState({
                    showAlertModal: true,
                    alertMessage: error.response.data,
                    loading: false,
                });
            });
    };

    _hideAlertModal = () => {
        this.setState({
            showAlertModal: false,
        });
    };

    render() {
        return (
            <Modal show={this.props.show} onHide={this.props.handleClose}>
                <Modal.Header closeButton>
                    <Modal.Title>Menu Board - {this.state.menuName} - Add Media</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Row>
                        <Col md={12}>
                            <FormGroup controlId="mediaType" className={css(styles.flex1)}>
                                <ControlLabel>{strings.mediaType}</ControlLabel>
                                <StyledDropdown
                                    id="mediaType"
                                    value={this.state.mediaType}
                                    onChange={this._handleChangeMediaTypeDropdown}
                                    options={mediaTypeOptions}
                                    placeholder={strings.selectMediaType}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    {this.state.mediaType !== "" && (
                        <div>
                            <Row>
                                <Col md={12}>
                                    <FormGroup controlId="name">
                                        <ControlLabel>{strings.name}</ControlLabel>
                                        <FormControl
                                            type="text"
                                            value={this.state.nameInput}
                                            onChange={this._handleNameChange}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={12}>
                                    <FormGroup controlId="description">
                                        <ControlLabel>{strings.description}</ControlLabel>
                                        <FormControl
                                            type="text"
                                            value={this.state.descriptionInput}
                                            onChange={this._handleDescriptionChange}
                                            componentClass="textarea"
                                            rows={2}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            {this.state.mediaType === "image" && (
                                <Row>
                                    <Col md={12}>
                                        <FormGroup controlId="duration">
                                            <ControlLabel>{strings.duration}</ControlLabel>
                                            <FormControl
                                                type="number"
                                                value={this.state.durationInput}
                                                onChange={this._handleDurationChange}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                            )}
                        </div>
                    )}
                    {this.state.mediaType === "image" && (
                        <ImageSetUploader
                            hideLabel
                            minWidth={this.props.width}
                            minHeight={this.props.height}
                            // Don't want to implement full typing with prop-types, especially if we plan to switch to TypeScript
                            // eslint-disable-next-line react/prop-types
                            previewImage={
                                this.state.uploadedImage ? this.state.uploadedImage.imageData : null
                            }
                            onSubmit={this._handleImageUpdate}
                        />
                    )}
                    {this.state.mediaType === "video" && (
                        <VideoUploader
                            hideTitle
                            requirements={{ width: this.props.width, height: this.props.height }}
                            onUpdate={this._handleVideoUpdate}
                        />
                    )}
                    <Row className={css(styles.buttonRow)}>
                        <Button bsStyle="link" onClick={this.props.handleClose}>
                            Cancel
                        </Button>
                        <Button
                            bsStyle="primary"
                            onClick={this._addFeatureAsset}
                            disabled={
                                !this.state.nameInput ||
                                !this.state.mediaType ||
                                (this.state.mediaType === "image" && !this.state.uploadedImage) ||
                                (this.state.mediaType === "video" && !this.state.uploadedVideo) ||
                                this.state.loading
                            }
                        >
                            Save
                        </Button>
                    </Row>
                </Modal.Body>
                <AlertModal
                    show={this.state.showAlertModal}
                    alertMessage={this.state.alertMessage}
                    handleClose={this._hideAlertModal}
                />
            </Modal>
        );
    }
}
